import { Calendar, LogOut, Mail } from 'lucide-react';
import {
  GlassCard,
  HelpAnchor,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { SEED_USER } from '@/versions/v1/shared/user-seed';
import { AccountNameField } from './AccountNameField';
import { formatLargeNumber, formatRelativeTime } from './format-utils';

// Embedding usage — not in settings-seed; local mock values for the account pane.
const EMBEDDING_USAGE = {
  totalCalls: 1_247,
  totalInputTokens: 892_000,
  lastCallAt: Date.UTC(2026, 6, 13),
  projectCount: 3,
};

const JOINED_DATE = new Date(Date.UTC(2025, 10, 12)).toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function UsageRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-[var(--glass-border)] px-5 py-4 last:border-b-0">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-zinc-900 dark:text-zinc-100">{value}</div>
    </div>
  );
}

function Row({
  Icon,
  label,
  value,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 border-b border-[var(--glass-border)] px-5 py-4 last:border-b-0">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium uppercase tracking-wide text-muted">
          {label}
        </div>
        <div className="mt-0.5 truncate text-sm text-zinc-900 dark:text-zinc-100">
          {value}
        </div>
      </div>
    </div>
  );
}

export function AccountScene() {
  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="AccountScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <SettingsPageHeader
              title="Account"
              description="Your profile and session."
              actions={<HelpAnchor doc="workspace/settings" />}
            />

            <Commentable name="ProfileCard">
              <GlassCard as="section" className="overflow-hidden">
                <AccountNameField initialName={SEED_USER.name} />
                <Row Icon={Mail} label="Email" value={SEED_USER.email} />
                <Row Icon={Calendar} label="Joined" value={JOINED_DATE} />
              </GlassCard>
            </Commentable>

            <Commentable name="SessionCard">
              <section>
                <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                  Session
                </h2>
                <GlassCard className="mt-3 flex items-start justify-between gap-4 p-5">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">
                      Sign out
                    </p>
                    <p className="mt-1 text-sm text-muted">
                      End your session on this device. You&apos;ll need to sign in
                      again to use the assistant.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = '/login';
                    }}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-700 transition-colors hover:border-red-300 hover:bg-red-50 dark:border-red-900/60 dark:bg-zinc-900 dark:text-red-400 dark:hover:border-red-900 dark:hover:bg-red-950/40"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </GlassCard>
              </section>
            </Commentable>

            <Commentable name="EmbeddingUsage">
              <section>
                <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                  Voyage embedding usage
                </h2>
                <GlassCard as="section" className="mt-3 overflow-hidden">
                  <UsageRow
                    label="Embedding API calls"
                    value={formatLargeNumber(EMBEDDING_USAGE.totalCalls)}
                  />
                  <UsageRow
                    label="Input tokens"
                    value={formatLargeNumber(EMBEDDING_USAGE.totalInputTokens)}
                  />
                  <UsageRow
                    label="Last call"
                    value={formatRelativeTime(EMBEDDING_USAGE.lastCallAt)}
                  />
                  <UsageRow
                    label="Projects"
                    value={`Across ${EMBEDDING_USAGE.projectCount} project${EMBEDDING_USAGE.projectCount === 1 ? '' : 's'}`}
                  />
                </GlassCard>
              </section>
            </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
